import type {
  FilterCondition,
  FilterConditionGroup,
  OperatorType,
} from '@/lib/crm/filters/types';
import type {
  AssociatedPropertyFilter,
  AssociationFilter,
  AssociationFilterOperator,
  CrmFilter,
  FilterGroup,
  FilterValue,
  PropertyFilter,
  PropertyFilterOperator,
} from '@/types/crm';
import {
  isAssociatedPropertyFilter,
  isAssociationFilter,
  isPropertyFilter,
} from '@/types/crm';
import type { SavedFilterViewWarning } from '@/types/savedFilterView';

/**
 * Maps frontend filter operators to backend filter_groups operators.
 * These operators are used in POST /query endpoint's filter_groups parameter.
 */
export const FRONTEND_TO_BACKEND_OPERATOR: Record<
  OperatorType,
  PropertyFilterOperator
> = {
  equals: 'exact',
  not_equals: 'ne',
  contains: 'contains',
  not_contains: 'not_contains',
  contains_any: 'contains_any',
  not_contains_any: 'not_contains_any',
  starts_with: 'startswith',
  ends_with: 'endswith',
  greater_than: 'gt',
  less_than: 'lt',
  greater_than_or_equal: 'gte',
  less_than_or_equal: 'lte',
  is_empty: 'isnull',
  is_not_empty: 'isnull',
  in: 'in',
  not_in: 'notin',
};

/**
 * Maps frontend filter operators to backend association filter operators.
 * Backend only supports 5 operators for associations: exact, ne, in, notin, isnull.
 * Frontend is_empty/is_not_empty map to isnull with boolean values.
 */
export const FRONTEND_TO_BACKEND_ASSOCIATION_OPERATOR: Record<
  string,
  AssociationFilterOperator
> = {
  equals: 'exact',
  not_equals: 'ne',
  in: 'in',
  not_in: 'notin',
  is_empty: 'isnull', // maps to isnull with value=true
  is_not_empty: 'isnull', // maps to isnull with value=false
};

/**
 * Inverse of FRONTEND_TO_BACKEND_OPERATOR.
 *
 * Note: 'isnull' is intentionally NOT included here because it maps to two
 * different frontend operators ('is_empty' and 'is_not_empty') depending on
 * the filter's boolean value. That case is handled explicitly in
 * filtersToConditions.
 *
 * Note: 'in_or_isnull' (association-only) has no frontend equivalent and is
 * intentionally absent. Filters using it are skipped with console.warn.
 */
export const BACKEND_TO_FRONTEND_OPERATOR: Record<string, OperatorType> = {
  exact: 'equals',
  ne: 'not_equals',
  contains: 'contains',
  not_contains: 'not_contains',
  contains_any: 'contains_any',
  not_contains_any: 'not_contains_any',
  startswith: 'starts_with',
  endswith: 'ends_with',
  gt: 'greater_than',
  lt: 'less_than',
  gte: 'greater_than_or_equal',
  lte: 'less_than_or_equal',
  in: 'in',
  notin: 'not_in',
};

/**
 * Checks if a filter value should be skipped (is empty/invalid).
 * Skips null, undefined, and empty strings.
 * Preserves structured values like QuantityValue and PipelineStageValue.
 */
function isEmptyValue(value: FilterValue): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Creates an association filter from a filter condition.
 * Returns null if the condition doesn't have an association or has an unsupported operator.
 */
function createAssociationFilter(
  condition: FilterCondition
): AssociationFilter | null {
  if (!condition.association) {
    return null;
  }

  const backendOperator =
    FRONTEND_TO_BACKEND_ASSOCIATION_OPERATOR[condition.operator];
  if (!backendOperator) {
    console.warn(`Unsupported association operator: ${condition.operator}`);
    return null;
  }

  // Handle is_empty operator -> isnull with value=true
  if (condition.operator === 'is_empty') {
    return {
      association: condition.association,
      operator: 'isnull',
      value: true,
    };
  }

  // Handle is_not_empty operator -> isnull with value=false
  if (condition.operator === 'is_not_empty') {
    return {
      association: condition.association,
      operator: 'isnull',
      value: false,
    };
  }

  // Validate value exists for non-isnull operators
  if (condition.value === null || condition.value === undefined) {
    return null;
  }

  // Handle in/not_in operators - ensure value is array of entity_ids
  if (condition.operator === 'in' || condition.operator === 'not_in') {
    if (!Array.isArray(condition.value)) {
      // Wrap single value in array
      return {
        association: condition.association,
        operator: backendOperator,
        value: [condition.value as string],
      };
    }
    return {
      association: condition.association,
      operator: backendOperator,
      value: condition.value as string[],
    };
  }

  // Handle equals/not_equals operators - value must be single entity_id
  if (condition.operator === 'equals' || condition.operator === 'not_equals') {
    // If array provided, take first element
    const singleValue = Array.isArray(condition.value)
      ? condition.value[0]
      : condition.value;

    if (typeof singleValue !== 'string') {
      console.warn(
        `Association equals/not_equals requires string value, got ${typeof singleValue}`
      );
      return null;
    }

    return {
      association: condition.association,
      operator: backendOperator,
      value: singleValue,
    };
  }

  return null;
}

/**
 * Canonical comma-split used at both the query boundary and the filter tooltip's display normalization.
 */
export function splitCommaSeparatedValue(value: string): string[] {
  return value.split(',').map(v => v.trim());
}

/**
 * Drops blank fragments from a `contains_any` / `not_contains_any` value list.
 *
 * These operators OR their fragments together as substring matches, so a blank
 * fragment matches every stored value: it silently widens `contains_any` to
 * "has any value" and collapses `not_contains_any` to "matches nothing" — the
 * opposite of what the user built. The multi-value editor seeds a trailing empty
 * row, so this is reachable simply by clicking "Add value" and not typing in it.
 *
 * Kept fragments are returned untrimmed, matching single-value `contains`, which
 * treats the fragment as raw. `in`/`not_in` deliberately do NOT use this: they
 * are whole-value exact matches where `''` matches nothing and is harmless.
 */
function dropBlankFragments(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (entry): entry is string => typeof entry === 'string' && entry.trim() !== ''
  );
}

/**
 * Creates an associated-property filter from a filter condition.
 *
 * Used when a condition targets a PROPERTY of an associated record
 * (`condition.association` + `condition.target_property`). The operator/value
 * semantics mirror a normal property filter (driven by the target property's
 * field type), NOT the id-based association operators. Returns null when the
 * condition is incomplete or the operator is unsupported.
 */
function createAssociatedPropertyFilter(
  condition: FilterCondition
): AssociatedPropertyFilter | null {
  if (!condition.association || !condition.target_property) {
    return null;
  }

  // is_empty / is_not_empty map to isnull true/false (no value needed).
  if (condition.operator === 'is_empty') {
    return {
      association: condition.association,
      target_property: condition.target_property,
      operator: 'isnull',
      value: true,
    };
  }
  if (condition.operator === 'is_not_empty') {
    return {
      association: condition.association,
      target_property: condition.target_property,
      operator: 'isnull',
      value: false,
    };
  }

  const backendOperator = FRONTEND_TO_BACKEND_OPERATOR[condition.operator];
  if (!backendOperator) {
    console.warn(
      `Unsupported associated-property operator: ${condition.operator}`
    );
    return null;
  }

  // Skip conditions with empty values (incomplete).
  if (isEmptyValue(condition.value)) {
    return null;
  }

  // in/not_in expect an array value (mirror createPropertyFilter).
  if (condition.operator === 'in' || condition.operator === 'not_in') {
    let arrayValue: PropertyFilter['value'];
    if (Array.isArray(condition.value)) {
      arrayValue = condition.value as PropertyFilter['value'];
    } else if (typeof condition.value === 'string') {
      arrayValue = splitCommaSeparatedValue(condition.value);
    } else {
      arrayValue = [condition.value] as PropertyFilter['value'];
    }
    return {
      association: condition.association,
      target_property: condition.target_property,
      operator: backendOperator,
      value: arrayValue,
    };
  }

  // contains_any/not_contains_any are list-valued here too — a text target
  // property offers them, so the multi-value editor renders and can emit a
  // blank fragment. Same hazard as createPropertyFilter; see dropBlankFragments.
  if (
    condition.operator === 'contains_any' ||
    condition.operator === 'not_contains_any'
  ) {
    return {
      association: condition.association,
      target_property: condition.target_property,
      operator: backendOperator,
      value: dropBlankFragments(
        typeof condition.value === 'string'
          ? splitCommaSeparatedValue(condition.value)
          : condition.value
      ),
    };
  }

  return {
    association: condition.association,
    target_property: condition.target_property,
    operator: backendOperator,
    value: condition.value as PropertyFilter['value'],
  };
}

/**
 * Creates a property filter from a filter condition.
 */
function createPropertyFilter(
  condition: FilterCondition
): PropertyFilter | null {
  const backendOperator = FRONTEND_TO_BACKEND_OPERATOR[condition.operator];

  // Handle is_empty and is_not_empty operators
  if (condition.operator === 'is_empty') {
    return {
      property: condition.field,
      operator: 'isnull',
      value: true,
    };
  }

  if (condition.operator === 'is_not_empty') {
    return {
      property: condition.field,
      operator: 'isnull',
      value: false,
    };
  }

  // Skip conditions with empty values
  if (isEmptyValue(condition.value)) {
    return null;
  }

  // Handle in/not_in/contains_any/not_contains_any operators - ensure value is an array
  if (
    condition.operator === 'in' ||
    condition.operator === 'not_in' ||
    condition.operator === 'contains_any' ||
    condition.operator === 'not_contains_any'
  ) {
    let arrayValue: PropertyFilter['value'];

    if (Array.isArray(condition.value)) {
      // Already an array - use as-is (could be structured values)
      arrayValue = condition.value as PropertyFilter['value'];
    } else if (typeof condition.value === 'string') {
      // Split comma-separated string into array
      arrayValue = splitCommaSeparatedValue(condition.value);
    } else {
      // Single value - wrap in array
      arrayValue = [condition.value] as PropertyFilter['value'];
    }

    if (
      condition.operator === 'contains_any' ||
      condition.operator === 'not_contains_any'
    ) {
      // See dropBlankFragments: a blank substring fragment matches everything.
      // An all-blank list collapses to [], which the query service defines as
      // "contains_any matches nothing / not_contains_any excludes nothing".
      arrayValue = dropBlankFragments(arrayValue);
    }

    return {
      property: condition.field,
      operator: backendOperator,
      value: arrayValue,
    };
  }

  // Standard operators - pass value through unchanged
  // This preserves structured values like { amount, unit } and { pipeline_name, stage_name }
  return {
    property: condition.field,
    operator: backendOperator,
    value: condition.value as PropertyFilter['value'],
  };
}

/**
 * Converts an array of FilterConditions to a flat array of CrmFilters,
 * applying the same per-clause helpers used across the module.
 *
 * Structured values (QuantityValue) are passed through unchanged
 * to preserve the full data structure expected by the backend.
 */
function conditionsToFilters(conditions: FilterCondition[]): CrmFilter[] {
  const filters: CrmFilter[] = [];

  for (const condition of conditions) {
    if (condition.association) {
      // Association-based: either by a target record's property
      // (target_property set) or by the associated record's id.
      const filter = condition.target_property
        ? createAssociatedPropertyFilter(condition)
        : createAssociationFilter(condition);
      if (filter) {
        filters.push(filter);
      }
    } else {
      // Property filter (existing logic)
      const filter = createPropertyFilter(condition);
      if (filter) {
        filters.push(filter);
      }
    }
  }

  return filters;
}

/**
 * Converts group-native UI condition groups to backend filter_groups.
 * OR between groups, AND within each group.
 *
 * Reuses conditionsToFilters per group (which applies the same per-clause
 * helpers and skips invalid/empty clauses). A group that produces zero valid
 * filters is OMITTED — an empty AND-group must never become a match-all
 * constraint. When every group is empty, returns { filter_groups: [] }.
 */
export function convertConditionGroupsToFilterGroups(
  groups: FilterConditionGroup[]
): { filter_groups: FilterGroup[] } {
  const filter_groups: FilterGroup[] = [];
  for (const group of groups) {
    const filters = conditionsToFilters(group.conditions);
    if (filters.length > 0) {
      filter_groups.push({ filters });
    }
  }
  return { filter_groups };
}

/**
 * Converts a flat array of CrmFilters (from one filter_group) back into
 * UI-shape FilterCondition[].
 *
 * Lossy / asymmetric mappings:
 * - `isnull:true` ↔ `is_empty` (resulting condition has value: null).
 * - `isnull:false` ↔ `is_not_empty` (resulting condition has value: null).
 *   The forward path ignores the original condition.value for these operators
 *   and emits only the boolean, so for round-trip equality the input must
 *   already use value: null.
 * - Association filters lose the original `field` on the forward path;
 *   reverse derives `field = filter.association`.
 *
 * Unsupported operators (e.g., association-only `in_or_isnull`) are skipped
 * with a console.warn rather than approximated.
 *
 * Generated condition ids use a stable `hydrated-${index}` format based on
 * output position (skipped filters do not consume an id slot).
 */
function filtersToConditions(filters: CrmFilter[]): FilterCondition[] {
  const out: FilterCondition[] = [];

  for (const filter of filters) {
    // Associated-property filters also carry an `association` key, so they
    // MUST be matched before isAssociationFilter / isPropertyFilter.
    if (isAssociatedPropertyFilter(filter)) {
      if (filter.operator === 'isnull') {
        const operator: OperatorType = filter.value
          ? 'is_empty'
          : 'is_not_empty';
        out.push({
          id: `hydrated-${out.length}`,
          field: '',
          operator,
          value: null,
          association: filter.association,
          target_property: filter.target_property,
        });
      } else {
        const mapped = BACKEND_TO_FRONTEND_OPERATOR[filter.operator];
        if (!mapped) {
          console.warn(
            `Unsupported associated-property operator: ${filter.operator}`
          );
          continue;
        }
        out.push({
          id: `hydrated-${out.length}`,
          field: '',
          operator: mapped,
          value: filter.value as FilterCondition['value'],
          association: filter.association,
          target_property: filter.target_property,
        });
      }
    } else if (isPropertyFilter(filter)) {
      if (filter.operator === 'isnull') {
        const operator: OperatorType = filter.value
          ? 'is_empty'
          : 'is_not_empty';
        out.push({
          id: `hydrated-${out.length}`,
          field: filter.property,
          operator,
          value: null,
        });
      } else {
        const mapped = BACKEND_TO_FRONTEND_OPERATOR[filter.operator];
        if (!mapped) {
          console.warn(`Unsupported property operator: ${filter.operator}`);
          continue;
        }
        out.push({
          id: `hydrated-${out.length}`,
          field: filter.property,
          operator: mapped,
          value: filter.value as FilterCondition['value'],
        });
      }
    } else if (isAssociationFilter(filter)) {
      if (filter.operator === 'isnull') {
        const operator: OperatorType = filter.value
          ? 'is_empty'
          : 'is_not_empty';
        out.push({
          id: `hydrated-${out.length}`,
          field: filter.association,
          operator,
          value: null,
          association: filter.association,
        });
      } else if (filter.operator === 'in_or_isnull') {
        console.warn(`Unsupported association operator: ${filter.operator}`);
        continue;
      } else {
        const mapped = BACKEND_TO_FRONTEND_OPERATOR[filter.operator];
        if (!mapped) {
          console.warn(`Unsupported association operator: ${filter.operator}`);
          continue;
        }
        out.push({
          id: `hydrated-${out.length}`,
          field: filter.association,
          operator: mapped,
          value: filter.value as FilterCondition['value'],
          association: filter.association,
        });
      }
    }
  }

  return out;
}

/**
 * Converts backend filter_groups to group-native UI condition groups,
 * PRESERVING group boundaries (no flattening). Each input group becomes exactly
 * one FilterConditionGroup (group count is preserved even if a group yields zero
 * conditions). Group ids are index-based (`group-${i}`).
 *
 * Condition ids are made GLOBALLY unique across groups (`hydrated-${i}-${j}`).
 * `filtersToConditions` restarts its `hydrated-${n}` counter at 0 for each
 * per-group call, so without this remap two conditions in different groups
 * would share the same id — and the builder's id-keyed edit/remove
 * (`updateConditionById`/`removeConditionById` match by id across ALL groups)
 * would silently mutate the same-index condition in every group. Prefixing with
 * the group index keeps ids unique so a builder edit stays scoped to one group.
 *
 * Unsupported operators (e.g. association `in_or_isnull`) are skipped with
 * console.warn by the reused per-clause logic and never corrupt sibling groups.
 */
export function filterGroupsToConditionGroups(
  filterGroups?: FilterGroup[] | null
): FilterConditionGroup[] {
  if (!filterGroups || filterGroups.length === 0) {
    return [];
  }
  return filterGroups.map((group, i) => ({
    id: `group-${i}`,
    conditions: filtersToConditions(group.filters).map((condition, j) => ({
      ...condition,
      id: `hydrated-${i}-${j}`,
    })),
  }));
}

/**
 * Merges user filter_groups with default filter_groups using Cartesian product.
 * Preserves OR semantics between default groups by creating all combinations.
 * Default groups are skipped if the user already filters on the same association.
 *
 * Example:
 *   defaults = [[A], [B]] (A OR B)
 *   user = [[C]]
 *   result = [[C, A], [C, B]] ((C AND A) OR (C AND B))
 *
 * @param userFilterGroups - Filter groups from user's conditions
 * @param defaultFilterGroups - Filter groups from config.defaultFilters
 * @returns Merged filter_groups preserving OR relationships
 */
export function mergeWithDefaultFilters(
  userFilterGroups: FilterGroup[] | undefined,
  defaultFilterGroups: FilterGroup[] | undefined
): FilterGroup[] {
  // No defaults to merge
  if (!defaultFilterGroups || defaultFilterGroups.length === 0) {
    return userFilterGroups ?? [];
  }

  // No user filters - use defaults as-is
  if (!userFilterGroups || userFilterGroups.length === 0) {
    return defaultFilterGroups;
  }

  // Collect association names from user's filters
  const userAssociations = new Set<string>();
  for (const group of userFilterGroups) {
    for (const filter of group.filters) {
      if (isAssociationFilter(filter)) {
        userAssociations.add(filter.association);
      }
    }
  }

  // Filter out default groups where the user is already filtering on that association
  const defaultGroupsToAdd: FilterGroup[] = [];
  for (const group of defaultFilterGroups) {
    // Check if this default group's associations are already covered by user
    const groupAssociations = new Set<string>();
    for (const filter of group.filters) {
      if (isAssociationFilter(filter)) {
        groupAssociations.add(filter.association);
      }
    }

    // If user doesn't filter on ANY of this group's associations, include the group
    const isOverridden = Array.from(groupAssociations).some(assoc =>
      userAssociations.has(assoc)
    );
    if (!isOverridden) {
      defaultGroupsToAdd.push(group);
    }
  }

  // Nothing to add
  if (defaultGroupsToAdd.length === 0) {
    return userFilterGroups;
  }

  // Apply defaults by creating a Cartesian product: each user group combined with each default group.
  // Filter semantics: OR between groups, AND within groups.
  // If defaults = [[A], [B]] and user = [[C]], result = [[C, A], [C, B]]
  // This means: (C AND A) OR (C AND B), preserving the OR relationship between A and B.
  const result: FilterGroup[] = [];
  for (const userGroup of userFilterGroups) {
    for (const defaultGroup of defaultGroupsToAdd) {
      result.push({
        ...userGroup,
        filters: [...userGroup.filters, ...defaultGroup.filters],
      });
    }
  }
  return result;
}

/**
 * ANDs a non-negotiable baseline (from a built-in view) into every user
 * OR-group. Unlike `mergeWithDefaultFilters`, it NEVER skips a baseline clause
 * on association-overlap and NEVER Cartesian-splits groups: a built-in view's
 * baseline is invisible and non-removable, so every baseline clause must always
 * apply on top of whatever the user filters on.
 *
 * - baseline empty  -> user groups unchanged
 * - user empty      -> a single group containing just the baseline filters
 * - otherwise       -> baseline filters appended to each user group (AND)
 *
 * @param baselineGroups - A SINGLE-GROUP array of filters. Passing multiple groups
 *   is not supported: the inter-group OR relationship is not preserved (all baseline
 *   filters are merged into a single flat array). Current callers always pass a
 *   single group.
 */
export function andBaselineIntoGroups(
  userGroups: FilterGroup[] | undefined,
  baselineGroups: FilterGroup[] | undefined
): FilterGroup[] {
  if (
    process.env.NODE_ENV !== 'production' &&
    baselineGroups &&
    baselineGroups.length > 1
  ) {
    console.error(
      '[andBaselineIntoGroups] multiple baseline groups are not supported; OR semantics between groups are lost. Pass a single group.'
    );
  }
  const baselineFilters: CrmFilter[] = (baselineGroups ?? []).flatMap(
    g => g.filters
  );
  const user = userGroups ?? [];
  if (baselineFilters.length === 0) {
    return user;
  }
  if (user.length === 0) {
    return [{ filters: baselineFilters }];
  }
  return user.map(g => ({ ...g, filters: [...g.filters, ...baselineFilters] }));
}

/**
 * Deterministic JSON serialization with object keys sorted recursively.
 *
 * Plain JSON.stringify is insertion-order-sensitive: a clause parsed from a
 * server response (`{"value":…,"operator":…,"property":…}`) and the same
 * clause constructed locally (`{property, operator, value}`) stringify
 * differently and would falsely compare as unequal.
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'null';
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const record = value as Record<string, unknown>;
  const entries = Object.keys(record)
    .sort()
    .filter(key => record[key] !== undefined)
    .map(key => `${JSON.stringify(key)}:${stableStringify(record[key])}`);
  return `{${entries.join(',')}}`;
}

/**
 * Structural comparator over CrmFilter clauses used to build an
 * order-insensitive canonical form (extracted from hasUserFilters).
 */
const compareFilters = (a: CrmFilter, b: CrmFilter): number => {
  // Type ordering: property < association < associated-property. Without an
  // explicit rule for mixed-type pairs the comparator falls through to
  // `return 0`, making the sort non-deterministic and causing
  // canonical comparison to report false positives when only the filter order
  // differs.
  if (isPropertyFilter(a) && isAssociationFilter(b)) {
    return -1;
  }
  if (isAssociationFilter(a) && isPropertyFilter(b)) {
    return 1;
  }
  if (isAssociatedPropertyFilter(a) && !isAssociatedPropertyFilter(b)) {
    return 1;
  }
  if (!isAssociatedPropertyFilter(a) && isAssociatedPropertyFilter(b)) {
    return -1;
  }

  if (isPropertyFilter(a) && isPropertyFilter(b)) {
    // Compare property name
    if (a.property !== b.property) {
      return a.property.localeCompare(b.property);
    }
    // Compare operator
    if (a.operator !== b.operator) {
      return a.operator.localeCompare(b.operator);
    }
    // Compare value (stringify for complex values)
    return stableStringify(a.value).localeCompare(stableStringify(b.value));
  }

  if (isAssociationFilter(a) && isAssociationFilter(b)) {
    // Compare association name
    if (a.association !== b.association) {
      return a.association.localeCompare(b.association);
    }
    // Compare operator
    if (a.operator !== b.operator) {
      return a.operator.localeCompare(b.operator);
    }
    // Compare value (stringify for complex values)
    return stableStringify(a.value).localeCompare(stableStringify(b.value));
  }

  if (isAssociatedPropertyFilter(a) && isAssociatedPropertyFilter(b)) {
    // Compare association name, then target property, operator, value.
    if (a.association !== b.association) {
      return a.association.localeCompare(b.association);
    }
    if (a.target_property !== b.target_property) {
      return a.target_property.localeCompare(b.target_property);
    }
    if (a.operator !== b.operator) {
      return a.operator.localeCompare(b.operator);
    }
    return stableStringify(a.value).localeCompare(stableStringify(b.value));
  }

  return 0;
};

/**
 * Order-insensitive canonical string form of a set of filter groups.
 *
 * Clauses are sorted structurally within each group and groups are sorted by
 * their serialized clause list, so two filter sets that differ only in clause
 * or group ordering canonicalize identically. Serialization is
 * key-order-insensitive (stableStringify), so a clause parsed from a server
 * response and the identical clause built locally canonicalize the same.
 * Used for structural equality (hasUserFilters, saved-view dirty tracking,
 * URL param diffing).
 */
export function canonicalizeFilterGroups(groups: FilterGroup[]): string {
  return stableStringify(
    groups
      .map(g => ({
        filters: [...g.filters].sort(compareFilters),
      }))
      .sort((a, b) =>
        stableStringify(a.filters).localeCompare(stableStringify(b.filters))
      )
  );
}

/**
 * Result of partitioning filter groups by a surface's clause applicability.
 * Extracted from useSavedViews.applyView so the apply path and saved-view
 * dirty tracking share one implementation.
 */
export interface PartitionedFilterGroups {
  /** Groups containing only the applicable clauses; empty groups omitted. */
  applicableGroups: FilterGroup[];
  /** Number of individual clauses dropped as inapplicable. */
  droppedCount: number;
  /** Number of non-empty groups whose every clause was inapplicable. */
  droppedGroupCount: number;
  /** One client-side warning per dropped clause. */
  droppedWarnings: SavedFilterViewWarning[];
}

/**
 * Partitions CrmFilter clauses group-by-group, preserving OR semantics:
 * inapplicable clauses are dropped from their group; a group whose clauses are
 * all inapplicable is dropped entirely (an empty AND-group must never become a
 * match-all constraint).
 *
 * `isApplicable` defaults to all-applicable. A throwing predicate propagates —
 * callers that must not reject (useSavedViews.applyView) wrap this call.
 */
export function partitionApplicableFilterGroups(
  groups: FilterGroup[],
  isApplicable?: (clause: CrmFilter) => boolean
): PartitionedFilterGroups {
  const applicable = isApplicable ?? (() => true);
  let droppedCount = 0;
  let droppedGroupCount = 0;
  const applicableGroups: FilterGroup[] = [];
  const droppedWarnings: SavedFilterViewWarning[] = [];

  for (const group of groups) {
    const kept: CrmFilter[] = [];
    for (const clause of group.filters) {
      if (applicable(clause)) {
        kept.push(clause);
      } else {
        droppedCount++;
        droppedWarnings.push({
          code: 'not_applicable',
          message: `A filter clause is not supported on this view and was dropped.`,
          dropped_clause: clause,
        });
      }
    }
    if (kept.length > 0) {
      applicableGroups.push({ filters: kept });
    } else if (group.filters.length > 0) {
      // All clauses in this group were inapplicable — entire group dropped.
      droppedGroupCount++;
    }
  }

  return { applicableGroups, droppedCount, droppedGroupCount, droppedWarnings };
}

/**
 * Determines if the current filter_groups contain user-applied filters
 * beyond the default filters.
 *
 * @param currentFilterGroups - Current filter_groups in state
 * @param defaultFilterGroups - Default filter_groups from config
 * @returns true if user has applied additional filters beyond defaults
 */
export function hasUserFilters(
  currentFilterGroups: FilterGroup[] | undefined,
  defaultFilterGroups: FilterGroup[] | undefined
): boolean {
  // If no current filters, check if there were defaults (shouldn't happen)
  if (!currentFilterGroups || currentFilterGroups.length === 0) {
    return false;
  }

  // If no defaults, any current filters are user-applied
  if (!defaultFilterGroups || defaultFilterGroups.length === 0) {
    return true;
  }

  // Count total filters
  const currentFilterCount = currentFilterGroups.reduce(
    (sum, group) => sum + group.filters.length,
    0
  );
  const defaultFilterCount = defaultFilterGroups.reduce(
    (sum, group) => sum + group.filters.length,
    0
  );

  // If counts differ, user has added filters
  if (currentFilterCount !== defaultFilterCount) {
    return true;
  }

  // Same count - compare order-insensitive canonical forms.
  return (
    canonicalizeFilterGroups(currentFilterGroups) !==
    canonicalizeFilterGroups(defaultFilterGroups)
  );
}

// Re-export type guards for convenience
export { isAssociatedPropertyFilter, isAssociationFilter, isPropertyFilter };
